import z from "zod";
import { TFunction } from "next-i18next";
import { CreditTypeEnum } from "@entities/Product/Application/types";
import { ISelectOption } from "@shared/types";

export const creditInfoSchema = (t: TFunction) =>
  z.object({
    credit_sum: z
      .string()
      .trim()
      .min(1, { message: t("Обязательное поле") }),
    credit_period: z.string().min(1, { message: t("Выберите период") }),
    credit_monthly_payment: z
      .string()
      .trim()
      .min(1, { message: t("Обязательное поле") }),
    credit_target: z.string().min(1, { message: t("Выберите цель кредита") }),
  });
export const creditUsedCarSchema = () =>
  z.object({
    credit_car_year: z.number().min(1900).max(2030),
    credit_car_doc_number: z.string().min(1),
  });
export const creditNewCarSchema = () =>
  z.object({
    credit_car_brand: z.string().min(1),
    credit_car_name: z.string().min(1),
    credit_car_type: z.string().min(1),
    credit_car_price: z.string().min(1),
    advance_payment: z.string(),
  });
export const collateralRealEstateSchema = () =>
  z.object({
    pledge_type: z.string().min(1),
    real_estate_price: z.string().min(2),
    real_estate_address_state: z.string().min(1),
    real_estate_address_district: z.string().min(1),
    real_estate_address_village: z.string(),
    real_estate_full_address: z.string().min(1),
  });

export const collateralCarSchema = () =>
  z.object({
    collateral_car_brand: z.string().min(1),
    collateral_car_name: z.string().min(1),
    collateral_car_type: z.string().min(1),
    collateral_car_year: z.number().min(1900).max(2030),
    collateral_car_doc_number: z.string().min(1),
    collateral_car_price: z.string().min(1),
  });

export const applicationValidationStep1Schema = (t: TFunction) =>
  z.object({
    phone: z.string({ required_error: t("Введите номер телефона") }).refine(
      (value) => value?.replace(/[^0-9]/g, "").length === 11,
      () => ({ message: t("Введите номер телефона") })
    ),
    agreement: z.literal(true),
  });

export const applicationValidationStep1CodeSchema = (t: TFunction) =>
  z.object({
    verification_code: z.string().refine(
      (value) => value?.replace(/[^0-9]/g, "").length === 4,
      () => ({ message: t("Введите код") })
    ),
  });
export const applicationValidationStep2Schema = (t: TFunction) =>
  z.object({
    name: z
      .string()
      .trim()
      .min(1, { message: t("Обязательное поле") }),
    last_name: z
      .string()
      .trim()
      .min(1, { message: t("Обязательное поле") }),
    middle_name: z.string().trim().optional(),
    iin: z.string().refine(
      (value) => value?.replace(/[^0-9]/g, "").length === 12,
      () => ({ message: t("Введите ИИН") })
    ),
    city: z.string().min(1, { message: t("Выберите город") }),
  });
export const applicationValidationStep3Schema = (t: TFunction) =>
  z.union([
    z
      .object({ credit_type: z.literal(CreditTypeEnum.NEW_AUTO) })
      .and(creditInfoSchema(t))
      .and(creditNewCarSchema()),
    z
      .object({ credit_type: z.literal(CreditTypeEnum.USED_AUTO) })
      .and(creditInfoSchema(t))
      .and(creditNewCarSchema())
      .and(creditUsedCarSchema()),

    z
      .object({ credit_type: z.literal(CreditTypeEnum.COLATERAL_FREE) })
      .and(creditInfoSchema(t)),
    z
      .object({ credit_type: z.literal(CreditTypeEnum.PLEDGE_AUTO) })
      .and(creditInfoSchema(t)),
    z
      .object({ credit_type: z.literal(CreditTypeEnum.PLEDGE_REAL_ESTATE) })
      .and(creditInfoSchema(t)),
  ]);

export const applicationValidationStep4Schema = (t: TFunction) =>
  z.object({
    company_name: z
      .string()
      .trim()
      .min(1, { message: t("Обязательное поле") }),
    classification: z.string().min(1, { message: t("Выберите классификацию") }),
    position: z
      .string({ required_error: t("Обязательное поле") })
      .trim()
      .min(1),
    working_period: z
      .string()
      .refine((value) => value?.replace(/[^0-9]/g, "").length === 8),
    month_income: z
      .string()
      .trim()
      .min(1, { message: t("Обязательное поле") }),
  });

export const applicationValidationStep5Schema = (
  productId: CreditTypeEnum,
  t: TFunction
) => {
  if (productId === CreditTypeEnum.PLEDGE_REAL_ESTATE) {
    return collateralRealEstateSchema();
  }

  if (productId === CreditTypeEnum.PLEDGE_AUTO) {
    return collateralCarSchema();
  }
  return z.object({});
};

export const applicationValidationSchema = (
  productId: CreditTypeEnum,
  t: TFunction
) =>
  z
    .object({})
    .and(applicationValidationStep2Schema(t))
    .and(applicationValidationStep3Schema(t))
    .and(applicationValidationStep4Schema(t))
    .and(applicationValidationStep5Schema(productId, t));

export const getStepperData = (
  step: number,
  t: TFunction,
  productId?: string
) => {
  const isStep5Visible = [
    CreditTypeEnum.PLEDGE_AUTO,
    CreditTypeEnum.PLEDGE_REAL_ESTATE,
  ].includes(productId as CreditTypeEnum);

  switch (step) {
    case 0:
      return {
        step: t(`Шаг 1`),
        title: t(`Верификация номера телефона`),
        percent: isStep5Visible ? 20 : 25,
      };
    case 1:
      return {
        step: t(`Шаг 1`),
        title: t(`Верификация номера телефона`),
        percent: isStep5Visible ? 20 : 25,
      };
    case 2:
      return {
        step: t(`Шаг 2`),
        title: t(`Персональные данные`),
        percent: isStep5Visible ? 40 : 50,
      };
    case 3:
      return {
        step: t(`Шаг 3`),
        title: t(`Кредитная информация`),
        percent: isStep5Visible ? 60 : 75,
      };
    case 4:
      return {
        step: t(`Шаг 4`),
        title: t(`Информация о работе`),
        percent: isStep5Visible ? 80 : 100,
      };
    case 5:
      return {
        step: t(`Шаг 5`),
        title: t(`Информация о залоге`),
        percent: 100,
      };
    default:
      return { step: "", title: "", percent: 0 };
  }
};

export const findOption = (
  options: ISelectOption[],
  value: string
): ISelectOption => options.find((option) => option.value === value);
